import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  catchError,
  finalize,
  Observable,
  of,
  share,
  switchMap,
  throwError,
} from 'rxjs';
import { Router } from '@angular/router';
import apiEndpoints from './api-endpoints.misc';
import { getAuthToken } from './headers.misc';
import { storageHelper } from './storage.misc';
import { SweetAlertService } from '../services/sweetalert.service';
import { AuthService } from '../services/auth.service';
import { normalizeApiErrorPayload } from './api-error.misc';

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  private excludedUrls: string[];
  private filesUrls: string[];
  totalRequests = 0;
  requestsCompleted = 0;

  constructor(
    private authService: AuthService,
    private sweetAlertService: SweetAlertService,
    private router: Router
  ) {
    this.excludedUrls = [
      apiEndpoints.signinUrl,
      apiEndpoints.signupUrl,
      apiEndpoints.registerUrl,
      apiEndpoints.forgotPasswordUrl,
      apiEndpoints.resetPasswordUrl,
    ];

    this.filesUrls = [apiEndpoints.uploadProfileUrl];
  }

  notifyRequestSuccess(request: HttpRequest<any>) {
    if (this.excludedUrls.includes(request.url) || request.method === 'GET') return;
  }

  notifyRequestFailure(request: HttpRequest<any>) {
    if (this.excludedUrls.includes(request.url) || request.method === 'GET') return;
  }

  notifyAuthSuccess(authRequest: HttpRequest<any>, successData: any) {
    if (authRequest.url !== apiEndpoints.signinUrl) return;
    this.authService.onAuthSuccess(successData);
  }

  notifyAuthFailure(authRequest: HttpRequest<any>, errorData: any) {
    if (authRequest.url !== apiEndpoints.signinUrl) return;
    this.authService.onAuthFailure(errorData);
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.totalRequests = this.isValidUrlForInterceptor(request.url)
      ? this.totalRequests + 1
      : this.totalRequests;

    const token = getAuthToken();
    let clonedRequest: HttpRequest<any>;

    // ---------- FORM DATA ----------
    if (request.body instanceof FormData) {
      let headers = request.headers;

      if (token) {
        headers = headers.set('Authorization', `Bearer ${token}`);
      }

      // IMPORTANT: remove Content-Type if present.
      headers = headers.delete('Content-Type');

      clonedRequest = request.clone({ headers });
    }

    // ---------- PROTECTED URLS ----------
    else if (this.isValidUrlForInterceptor(request.url)) {
      if (!this.authService.isAuthenticated && this.authService.accessToken) {
        this.sweetAlertService.toastWarning(
          'Session expirée !',
          3000,
          'Veuillez vous connecter à nouveau'
        );
        storageHelper.local.clear();
        this.router.navigateByUrl(`/auth/signin`);
      }

      const hdr: any = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };

      if (token) hdr['Authorization'] = `Bearer ${token}`;

      clonedRequest = request.clone({ setHeaders: hdr });
    }

    // ---------- PUBLIC URLS ----------
    else {
      clonedRequest = request.clone({
        setHeaders: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });
    }

    return next.handle(clonedRequest).pipe(
      switchMap((event) => {
        if (event instanceof HttpResponse) {
          this.notifyRequestSuccess(clonedRequest);
          this.notifyAuthSuccess(clonedRequest, event.body);
        }
        return of(event);
      }),
      catchError((rawError: HttpErrorResponse) => {
        const normalizedError = this.normalizeHttpError(rawError);

        this.notifyRequestFailure(clonedRequest);
        this.notifyAuthFailure(clonedRequest, normalizedError.error);

        if (normalizedError.status === 401) {
          this.sweetAlertService.toastWarning(
            'Accès non autorisé !',
            3000,
            'Veuillez vous connecter à nouveau'
          );
          storageHelper.local.clear();
          this.router.navigateByUrl(`/`);
        }

        return throwError(() => normalizedError);
      }),
      finalize(() => {
        this.requestsCompleted++;
        if (this.requestsCompleted === this.totalRequests) {
          this.totalRequests = 0;
          this.requestsCompleted = 0;
        }
      }),
      share()
    );
  }

  private isValidUrlForInterceptor(url: string) {
    return !this.excludedUrls.includes(url);
  }

  private normalizeHttpError(error: HttpErrorResponse): HttpErrorResponse {
    const normalizedPayload = normalizeApiErrorPayload(error.error, error.message);

    return new HttpErrorResponse({
      error: normalizedPayload,
      headers: error.headers,
      status: error.status,
      statusText: error.statusText,
      url: error.url || undefined,
    });
  }
}
